<template>
  <PForm
    ref="pForm"
    :heading="`${formState.edit ? 'View/Edit' : 'Add'} Organisation`"
    :primary-cta="hash ? {} : submitButton"
    :show-header="hash ? false : true"
    :secondary-cta="{}"
    @close="onClose"
    @submit.prevent="onSubmit"
    @reset="resetForm"
  >
    <template v-if="formState.edit" #header-info>
      <PBadge
        :label="formData.is_active ? 'Active' : 'Deactivate'"
        :type="formData.is_active ? 'Active' : 'Deactivate'"
        class="q-mx-md"
      />
    </template>
    <div class="row q-col-gutter-x-md">
      <div class="col-12">
        <PInput
          v-model="formData.pan"
          :label="'PAN'"
          :placeholder="'Enter PAN'"
          :validations="{ type: 'pan', length: 10 }"
          required
          :disable="formState.edit"
          class="q-pa-none"
          :loading="panLoading"
          maxlength="10"
          :error="panApiFailed"
          :error-message="computedPanErrorMessage"
          @update:model-value="handlePanUpdate"
        >
          <template #actionComponent>
            <PCheckbox
              v-model="computedGstUnregistered"
              label="Unregistered"
              left-label
              :disable="formState.edit"
            />
          </template>
        </PInput>
        <PBtn
          label="Get Details"
          flat
          text
          class="q-pa-none q-mb-lg"
          :class="{ 'q-mt-md': computedPanErrorMessage }"
          :disable="isGetDetailsDisabled"
          @click="fetchPanDetails"
        />
      </div>
      <div class="col-12">
        <PInput
          v-model.trim="formData.name"
          :label="'Registered Business Name'"
          :placeholder="'Enter Registered Business Name'"
          required
          :disable="isRegisteredBusinessNameDisabled"
          :loading="unregisteredBusinessLoading"
          @update:model-value="handleBusinessNameUpdate"
        />
      </div>

      <div v-if="formState.edit" class="col-12 q-mt-lg">
        <span class="text-md text-weight-medium text-neutral">
          Portfolio Overview
        </span>
        <q-separator class="q-mb-sm" />
      </div>
      <div v-if="formState.edit" class="col-12 q-mb-sm">
        <div class="row q-col-gutter-xl">
          <div class="col-6">
            <PInput
              :model-value="
                formatCurrency(formData.portfolio.outstanding_vendor_payables)
              "
              :label="'Outstanding Vendor Payables'"
              :placeholder="'Outstanding Vendor Payables'"
              required
              disable
              input-class="text-weight-bold"
            />
          </div>
          <div class="col-6">
            <PInput
              :model-value="
                formatCurrency(formData.portfolio.unused_vendor_credits)
              "
              :label="'Unused Vendor Credits'"
              :placeholder="'Unused Vendor Credits'"
              required
              disable
              input-class="text-weight-bold"
            />
          </div>
        </div>
      </div>
      <div v-if="formState.edit" class="col-12 q-mb-sm">
        <div class="row q-col-gutter-xl">
          <div class="col-6">
            <PInput
              :model-value="
                formatCurrency(
                  formData.portfolio.outstanding_customer_receivables,
                )
              "
              :label="'Outstanding Customer Receivables'"
              :placeholder="'Outstanding Customer Receivables'"
              required
              disable
              input-class="text-weight-bold"
            />
          </div>
          <div class="col-6">
            <PInput
              :model-value="
                formatCurrency(formData.portfolio.unused_customer_credits)
              "
              :label="'Unused Customer Credits'"
              :placeholder="'Unused Customer Credits'"
              required
              disable
              input-class="text-weight-bold"
            />
          </div>
        </div>
      </div>
      <div class="col-12 q-mt-lg">
        <span class="text-md text-weight-medium text-neutral">
          Tax Settings
        </span>
        <q-separator class="q-mb-sm" />
      </div>

      <div v-if="!hash || hash === '#customers'" class="col-12 q-mt-md">
        <div class="row q-col-gutter-xl">
          <div class="col-6">
            <PField v-model="isTCSApplicable" :label="'TCS Applicable?'">
              <template #control>
                <POptionGroup
                  v-model="isTCSApplicable"
                  :disable="isFieldDisabledForUser"
                  :options="[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]"
                />
              </template>
            </PField>
          </div>
          <div v-if="isTCSApplicable" class="col-6">
            <PSelectInput
              v-model="TCStaxSetting"
              :options="unref(TCStaxFilter.entities)"
              label="Tax Selection"
              option-label="name"
              :disable="isFieldDisabledForUser"
              placeholder="Select a tax"
              required
              @filter="TCStaxFilter.onFilter"
              @filter-abort="TCStaxFilter.onFilterAbort"
            />
          </div>
          <div v-else class="col-6"></div>
        </div>
      </div>
      <div v-if="!hash || hash === '#vendors'" class="col-12">
        <div class="row q-col-gutter-xl">
          <div class="col-6">
            <PField v-model="isTDSApplicable" :label="'TDS Applicable?'">
              <template #control>
                <POptionGroup
                  v-model="isTDSApplicable"
                  :disable="isFieldDisabledForUser"
                  :options="[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]"
                />
              </template>
            </PField>
          </div>

          <div v-if="isTDSApplicable" class="col-6">
            <PSelectInput
              v-model="TDStaxSetting"
              :options="unref(TDStaxFilter.entities)"
              label="Tax Selection"
              option-label="name"
              placeholder="Select a tax"
              :disable="isFieldDisabledForUser"
              required
              @filter="TDStaxFilter.onFilter"
              @filter-abort="TDStaxFilter.onFilterAbort"
            />
          </div>
          <div v-else class="col-6"></div>
        </div>
      </div>
      <div class="col-12 q-mt-lg">
        <span class="text-md text-weight-medium text-neutral">
          Payments Information
        </span>
        <q-separator class="q-mb-sm" />
      </div>
      <div class="col-4">
        <PSelectInput
          v-model="computedCreditCycle"
          :disable="isFieldDisabledForUser"
          :options="unref(paymentTermsFilter?.entities)"
          :validations="{ label: 'Credit Cycle' }"
          label="Credit Cycle (in days)"
          option-label="duration"
          required
          option-value="duration"
          label-suffix="unit"
          placeholder="Select credit cycle"
          @filter="paymentTermsFilter?.onFilter"
          @filter-abort="paymentTermsFilter?.onFilterAbort"
        />
      </div>
      <div v-if="!hash || hash === '#customers'" class="col-4">
        <PInput
          v-model.trim.number="formData.credit_limit"
          :label="'Credit Limit'"
          min="1"
          placeholder="Enter Credit Limit"
          :disable="isFieldDisabledForUser"
          :validations="{ type: 'integer', gte: 1 }"
          type="number"
        />
      </div>

      <div v-if="!hash || hash === '#vendors'" class="col-4">
        <PInput
          v-model.trim.number="formData.advance_limit"
          :label="'Advance Limit'"
          min="1"
          placeholder="Advance Limit"
          :disable="isFieldDisabledForUser"
          :validations="{ type: 'integer', gte: 1 }"
          type="number"
        />
      </div>
    </div>
    <template v-if="formState.edit" #footer-info>
      <PBtn
        v-if="formData.is_active"
        :label="'Deactivate'"
        color="error-70"
        class="q-mr-xs"
        flat
        :disabled="
          !permissions?.users_organisations?.activate || isDeactivationDisabled
        "
        @click="confirmAction('deactivate')"
      />
      <PBtn
        v-else-if="!formData.is_active"
        :label="'Activate'"
        color="success-70"
        class="q-mr-xs"
        flat
        :disable="!permissions?.users_organisations?.activate"
        @click="confirmAction('activate')"
      />
    </template>
  </PForm>
</template>
<script setup>
import { debounce, useQuasar } from "quasar";
import organisationFormModel from "src/modules/organisations/models/organisationForm";
import { useOrganisationStore } from "src/stores/organisation";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { useConfirmationOnSubmit } from "../composables/submit-confirmation-handler";
import { computed, unref } from "vue";
import { useRouterUtilities } from "src/composables/routerUtilities";
import { useBusinessTypes } from "src/composables/businessTypes";
import { capitalizePreservingSpecialChars } from "assets/scripts/utilities/helpers";
import { numberFormat } from "assets/scripts/utilities/helpers";

const $q = useQuasar();

const { removeQueryParams } = useRouterUtilities();
const { permissions } = storeToRefs(useAuthStore());
const { hash, queryParams } = useRouterUtilities();
const { formState, organisation, organisations } = storeToRefs(
  useOrganisationStore(),
);
const {
  postOrganisation,
  putOrganisation,
  toggleOrganisationDrawer,
  getUnregisteredOrg,
  getUserStatus,
  deactivateOrg,
  activateOrg,
  getPanDetail,
} = useOrganisationStore();
let TDStaxFilter = {};
let TCStaxFilter = {};
const pForm = ref(null);
const panLoading = ref(false);
const panApiFailed = ref(false);
const isTDSApplicable = ref(false);
const isTCSApplicable = ref(false);
const isDeactivationDisabled = ref(false);
const unregisteredBusinessLoading = ref(false);

const { selectBusinessType, getShopName } = useBusinessTypes();

const { formData, closeForm, submitForm, isLoading, resetForm } = useForm({
  formModel: organisationFormModel(),
  formRef: pForm,
  isForm: formState,
  entity: organisation,
  post: postOrganisation,
  put: putOrganisation,
  toggleForm: toggleOrganisationDrawer,
});

const { onSubmit } = useConfirmationOnSubmit({
  formState,
  formData,
  organisation,
  organisations,
  hash,
  submitForm,
  closeForm,
});

const onClose = async () => {
  removeQueryParams();

  panApiFailed.value = false;

  closeForm(true);
};

const isFormDisabled = computed(() => {
  return !formData.value.is_active;
});

const submitButton = computed(() => {
  if (unref(formState).view) {
    return {};
  } else {
    return {
      label: formState.value.edit ? "Update" : "Submit",
      loading: isLoading.value,
      disable: unref(isSubmitButtonDisabled),
    };
  }
});

const isSubmitButtonDisabled = computed(() => {
  return (
    unref(isFormDisabled) ||
    (formState.value.edit && !unref(organisation)?._id) ||
    gstValidationError.value ||
    panApiFailed.value ||
    formData.value.pan.length !== 10 ||
    !formData.value.name
  );
});

const computedPanErrorMessage = computed(() => {
  if (panApiFailed.value) return "No business found with this PAN";
  else return "";
});

const handlePanUpdate = (pan) => {
  formData.value.pan = pan.trim().toUpperCase();

  panApiFailed.value = false;
};

const handleBusinessNameUpdate = (name) => {
  formData.value.name = capitalizePreservingSpecialChars(name);

  debounceFetchUnregisteredData(name);
};

const debounceFetchUnregisteredData = debounce((name) => {
  getUnregisteredData(name);
}, 1200);

const existingOrganisationHandler = inject("existing-org", () => {});

const formatCurrency = (value) => {
  if (!value) return "₹ 0.00";

  return numberFormat({
                    number: value ?? 0,
                    style: "currency",
                  });
};

const fetchPanDetails = async () => {
  try {
    panLoading.value = true;

    panApiFailed.value = false;

    const response = await getPanDetail(unref(formData).pan);

    if (response?._id) {
      formState.value.edit = true;

      organisation.value = response;

      existingOrganisationHandler?.setExistingOrg?.(true);
    }

    const businessType = await selectBusinessType(
      response?.constitutionOfBusiness,
    );

    const name = getShopName(businessType?.code);

    if (unref(formState).edit) {
      organisation.value = {
        ...response,
        name: capitalizePreservingSpecialChars(response[name] || response.name),
      };
    } else {
      let gstinList = response?.gstins || [];

      delete response.gstins;

      formData.value = {
        ...unref(formData),
        ...response,
        gstinList: gstinList || [],
        name: capitalizePreservingSpecialChars(response[name] || response.name),
        is_active: true,
        _id: null,
        _v: null,
      };

      existingOrganisationHandler?.setExistingOrg?.(false);
    }
  } catch (error) {
    panApiFailed.value = true;

    console.error(error);
  } finally {
    panLoading.value = false;
  }
};

const getUnregisteredData = async (name) => {
  try {
    unregisteredBusinessLoading.value = true;

    const response = await getUnregisteredOrg(name);

    if (!response?._id) {
      formData.value = {
        ...unref(formData),
        tax_settings: [],
        credit_limit: 1,
        advance_limit: 1,
        credit_cycle: 0,
        is_active: true,
        _id: null,
        _v: null, // to not send it in api
      };

      return;
    }

    if (response._id) {
      formState.value.edit = true;

      organisation.value = response;
    }

    if (!unref(formState).edit) {
      formData.value = {
        ...unref(formData),
        ...response,
        is_active: true,
        _id: null,
        _v: null,
      };
    }
  } catch (error) {
    console.error("Error fetching unregistered data:", error);
  } finally {
    unregisteredBusinessLoading.value = false;
  }
};

const gstValidationError = computed(() => {
  if (formState.value.edit && organisation.value?._id) {
    const gstin = formData.value.gstin || "";
    const pan = formData.value.pan || "";

    if (gstin.length === 15 && pan.length === 10) {
      return gstin.substring(2, 12) !== pan;
    }
  }

  return false;
});

const isGetDetailsDisabled = computed(() => {
  const pan = formData.value.pan || "";

  const panRegex = /^[A-Z]{5}\d{4}[A-Z]$/i;
  const isPanInvalid = pan.length !== 10 || !panRegex.test(pan);

  return (
    isPanInvalid ||
    formState.value.edit ||
    panLoading.value ||
    computedGstUnregistered.value ||
    isFormDisabled.value ||
    (formState.value.edit && organisation.value?.gstin_business?.length === 15)
  );
});

const isRegisteredBusinessNameDisabled = computed(() => {
  return (
    (!computedGstUnregistered.value && !panApiFailed.value) ||
    unref(formState).edit ||
    isFormDisabled.value
  );
});

const isFieldDisabledForUser = computed(() => {
  return isFormDisabled.value || (unref(hash) && formState.value.edit);
});

const computedGstUnregistered = computed({
  get() {
    return !unref(formData).is_gst_registered;
  },
  set(isGstUnRegistered) {
    resetForm();

    panApiFailed.value = false;

    formData.value.is_gst_registered = !isGstUnRegistered;
  },
});

// Watcher for payment Terms
const computedCreditCycle = computed({
  get: () => {
    let paymentTerms = unref(formData).credit_cycle;

    paymentTerms =
      (paymentTerms || "0") + (paymentTerms > 1 ? " days" : " day");

    return paymentTerms;
  },
  set: (paymentTerms) => {
    unref(formData).credit_cycle = paymentTerms ?? 0;
  },
});

const paymentTermsFilter = useFilter({
  entity: "paymentTerms",
});

const initTaxFilter = (role) => {
  if (role == "buyer") {
    TCStaxFilter = useFilter({
      entity: "tax",
      options: {
        params: {
          roles: role,
        },
      },
    });
  } else if (role === "seller") {
    TDStaxFilter = useFilter({
      entity: "tax",
      options: {
        params: {
          roles: role,
        },
      },
    });
  }
};

const TDStaxSetting = computed({
  get: () => {
    const taxSettings = unref(formData)?.tax_settings;
    const tdsTax = taxSettings?.find((tax) => tax?.type === "tds");

    return tdsTax || "";
  },
  set: (value) => {
    const tdsTaxIndex = unref(formData)?.tax_settings?.findIndex(
      (tax) => tax?.type === "tds",
    );

    if (tdsTaxIndex === -1) {
      unref(formData)?.tax_settings?.push(value);
    } else {
      unref(formData).tax_settings[tdsTaxIndex] = value;
    }
  },
});

const TCStaxSetting = computed({
  get: () => {
    const taxSettings = unref(formData)?.tax_settings;
    const tcsTax = taxSettings?.find((tax) => tax?.type === "tcs");

    if (tcsTax?._id !== null) {
      return tcsTax;
    }

    return {};
  },
  set: (value) => {
    const tcsTaxIndex = unref(formData).tax_settings?.findIndex(
      (tax) => tax?.type === "tcs",
    );

    if (tcsTaxIndex === -1) {
      unref(formData).tax_settings?.push(value);
    } else {
      unref(formData).tax_settings[tcsTaxIndex] = value;
    }
  },
});

//deactivate disabled enlabled watcher

const userStatus = async () => {
  if (unref(formData)._id) {
    try {
      const response = await getUserStatus(unref(formData)._id);

      isDeactivationDisabled.value = !response;
    } catch (error) {
      console.error("Failed to fetch user status:", error);

      isDeactivationDisabled.value = true;
    }
  }
};

const confirmHandler = async (action) => {
  try {
    if (formData.value._id) {
      if (action === "deactivate") {
        await deactivateOrg(formData.value._id);

        formData.is_active = false;
      } else if (action === "activate") {
        await activateOrg(formData.value._id);

        formData.is_active = true;
      }

      closeForm(true);
    }
  } catch (error) {
    console.error(error);
  }
};

const confirmAction = (action) => {
  const isDeactivation = action === "deactivate";

  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      text: `Do you want to ${isDeactivation ? "Deactivate" : "Activate"} ${formData?.value.name}?`,
      labelClass: "text-center q-ma-xl rounded-borders",
      cancelLabel: "Cancel",
      onConfirm: () => confirmHandler(action),
    },
  });
};

defineExpose({
  onSubmit,
  pForm,
  onClose,
  orgData: computed(() => formData.value),
});

watch(
  () => unref(formData)?.tax_settings,
  (taxSettings) => {
    const tdsTax = taxSettings?.find((tax) => tax?.type === "tds");
    const tcsTax = taxSettings?.find((tax) => tax?.type === "tcs");

    isTDSApplicable.value = !!tdsTax;

    isTCSApplicable.value = !!tcsTax;
  },
);

watch(
  () => unref(formData)._id,
  () => {
    userStatus();
  },
);

watch(isTDSApplicable, (value) => {
  if (value) {
    initTaxFilter("seller");
  }

  const taxSettings = unref(formData)?.tax_settings;
  const tdsIndex = taxSettings?.findIndex((tax) => tax?.type === "tds");

  if (!value && tdsIndex !== -1) {
    taxSettings.splice(tdsIndex, 1);
  }
});

watch(isTCSApplicable, (value) => {
  if (value) {
    initTaxFilter("buyer");
  }

  const taxSettings = unref(formData)?.tax_settings;
  const tcsIndex = taxSettings?.findIndex((tax) => tax?.type === "tcs");

  if (!value && tcsIndex !== -1) {
    taxSettings.splice(tcsIndex, 1);
  }
});

watch(
  () => queryParams,
  (data) => {
    if (
      unref(hash) &&
      unref(data) &&
      unref(data)?.add &&
      unref(organisation)?._id
    ) {
      formState.value.edit = true;
    }
  },
  {
    immediate: true,
  },
);

const route = useRoute();
const isOrganisation = computed(() => route.path === "/organisations"); // do this path will be capital O -

provide("isOrganisation", isOrganisation);
</script>
